/**
 * Flash-loan triangular arb dashboard — cloud-only scanner/executor.
 */
import React, { Component, useCallback, useEffect, useState } from "react";
import {
  Box,
  Button,
  Heading,
  Switch,
  Text,
  VStack,
  HStack,
  Code,
  Alert,
  AlertIcon,
  Divider,
  NumberInput,
  NumberInputField,
} from "@chakra-ui/react";
import { useMoralis } from "react-moralis";
import Moralis from "moralis-v1";
import { Link as RouterLink } from "react-router-dom";

const DEFAULT_CONFIG = {
  enabled: true,
  executionEnabled: false,
  useFlashLoan: false,
  scanIntervalMs: 7000,
  minNetProfitUsd: 0.7,
  minNetProfitBps: 6,
};

const parseServerUrl = process.env.REACT_APP_SERVER_URL || "";
const parseAppId = process.env.REACT_APP_APPLICATION_ID || "";

class FlashArbErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  render() {
    if (this.state.error) {
      return (
        <Box p={8} color="white" bg="red.900" borderRadius="md" m={4}>
          <Heading size="md" mb={2}>
            Flash Arb failed to load
          </Heading>
          <Text fontFamily="mono" fontSize="sm">
            {String(this.state.error?.message || this.state.error)}
          </Text>
        </Box>
      );
    }
    return this.props.children;
  }
}

function FlashArbBotInner() {
  const { account, isAuthenticated } = useMoralis();
  const [config, setConfig] = useState(DEFAULT_CONFIG);
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const [autoScan, setAutoScan] = useState(false);
  const [error, setError] = useState("");

  const player = account ? String(account).toLowerCase() : "";
  const cloudReady = Boolean(parseServerUrl && parseAppId && Moralis.Cloud?.run);

  const refreshConfig = useCallback(async () => {
    if (!player || !cloudReady) return;
    try {
      const cfg = await Moralis.Cloud.run("getFlashArbConfig", { player });
      setConfig((prev) => ({ ...prev, ...cfg }));
    } catch (e) {
      console.warn("[FlashArb] getFlashArbConfig", e);
    }
  }, [player, cloudReady]);

  const refreshStatus = useCallback(async () => {
    if (!cloudReady) return;
    try {
      const params = player ? { player } : {};
      const s = await Moralis.Cloud.run("getFlashArbStatus", params);
      setStatus(s);
      if (s?.config) setConfig((prev) => ({ ...prev, ...s.config }));
    } catch (e) {
      console.warn("[FlashArb] getFlashArbStatus", e);
    }
  }, [player, cloudReady]);

  useEffect(() => {
    refreshConfig();
    refreshStatus();
  }, [refreshConfig, refreshStatus]);

  useEffect(() => {
    if (!autoScan || !cloudReady) return;
    const id = setInterval(() => {
      const params = { dryRun: true, ...(player ? { player } : {}) };
      void Moralis.Cloud.run("flashArbTick", params).then((r) =>
        setStatus((prev) => ({ ...(prev || {}), lastScan: r }))
      );
    }, config.scanIntervalMs || 7000);
    return () => clearInterval(id);
  }, [autoScan, player, config.scanIntervalMs, cloudReady]);

  const saveConfig = async () => {
    if (!player) {
      setError("Connect wallet to save per-wallet config.");
      return;
    }
    setBusy(true);
    setError("");
    try {
      await Moralis.Cloud.run("setFlashArbConfig", { player, configJSON: config });
      await refreshStatus();
    } catch (e) {
      setError(e.message || String(e));
    } finally {
      setBusy(false);
    }
  };

  const runScan = async (dryRun = true) => {
    if (!cloudReady) {
      setError("Parse/Moralis not configured (REACT_APP_SERVER_URL / REACT_APP_APPLICATION_ID).");
      return;
    }
    setBusy(true);
    setError("");
    try {
      const params = { dryRun, ...(player ? { player } : {}) };
      const result = await Moralis.Cloud.run("flashArbTick", params);
      setStatus((prev) => ({ ...(prev || {}), lastScan: result }));
    } catch (e) {
      const msg = e.message || String(e);
      setError(
        msg.includes("Invalid function")
          ? `${msg} — deploy Parse cloud (main.js + npm run build in reckless-command-center).`
          : msg
      );
    } finally {
      setBusy(false);
    }
  };

  const lastScan = status?.lastScan;
  const best = lastScan?.best;

  return (
    <Box
      position="relative"
      zIndex={10}
      bg="gray.900"
      minH="60vh"
      color="white"
      py={8}
      px={4}
      maxW="900px"
      mx="auto"
      borderRadius="md"
      borderWidth="1px"
      borderColor="gray.700"
    >
      <VStack align="stretch" spacing={6}>
        <HStack justify="space-between" flexWrap="wrap">
          <Heading size="lg" color="cyan.300">
            Flash Arb (USDC / WETH / USDT)
          </Heading>
          <Button as={RouterLink} to="/donotuse" size="sm" variant="outline" colorScheme="purple">
            Sniper Bot
          </Button>
        </HStack>

        <Text color="gray.400" fontSize="sm">
          Triangular arb on Arbitrum via Parse Cloud. Scan works without a wallet; connect to save config.
          Flash loans need <Code fontSize="xs">EXECUTOR_ADDRESS</Code> in backend{" "}
          <Code fontSize="xs">addresses.ts</Code>.
        </Text>

        {!cloudReady && (
          <Alert status="error">
            <AlertIcon />
            Missing REACT_APP_SERVER_URL or REACT_APP_APPLICATION_ID — cloud calls will not work.
          </Alert>
        )}

        {!isAuthenticated && (
          <Alert status="info">
            <AlertIcon />
            Wallet optional for dry scans. Connect (header) to save your config to Parse.
          </Alert>
        )}

        {error && (
          <Alert status="error">
            <AlertIcon />
            {error}
          </Alert>
        )}

        <Box borderWidth="1px" borderColor="gray.700" borderRadius="md" p={4}>
          <Text fontWeight="bold" mb={3}>
            Config
          </Text>
          <VStack align="stretch" spacing={3}>
            <HStack justify="space-between">
              <Text fontSize="sm">Module enabled</Text>
              <Switch
                isChecked={config.enabled !== false}
                onChange={(e) => setConfig((c) => ({ ...c, enabled: e.target.checked }))}
              />
            </HStack>
            <HStack justify="space-between">
              <Text fontSize="sm">Execution (live trades)</Text>
              <Switch
                colorScheme="red"
                isChecked={!!config.executionEnabled}
                onChange={(e) => setConfig((c) => ({ ...c, executionEnabled: e.target.checked }))}
              />
            </HStack>
            <HStack justify="space-between">
              <Text fontSize="sm">Use flash loan (Aave)</Text>
              <Switch
                isChecked={!!config.useFlashLoan}
                onChange={(e) => setConfig((c) => ({ ...c, useFlashLoan: e.target.checked }))}
              />
            </HStack>
            <HStack justify="space-between">
              <Text fontSize="sm">Auto-scan (dry)</Text>
              <Switch isChecked={autoScan} onChange={(e) => setAutoScan(e.target.checked)} />
            </HStack>
            <HStack>
              <Text fontSize="sm" flexShrink={0}>
                Min net profit USD
              </Text>
              <NumberInput
                size="sm"
                value={config.minNetProfitUsd ?? 0.7}
                min={0}
                step={0.1}
                onChange={(_, v) => {
                  const n = parseFloat(String(v));
                  if (Number.isFinite(n)) {
                    setConfig((c) => ({ ...c, minNetProfitUsd: n }));
                  }
                }}
              >
                <NumberInputField />
              </NumberInput>
            </HStack>
          </VStack>
          <Button mt={4} colorScheme="blue" onClick={saveConfig} isDisabled={!player || busy} width="full">
            Save config to cloud
          </Button>
        </Box>

        <HStack flexWrap="wrap" gap={2}>
          <Button colorScheme="teal" onClick={() => runScan(true)} isDisabled={busy || !cloudReady}>
            Scan now (dry run)
          </Button>
          <Button
            colorScheme="red"
            variant="outline"
            onClick={() => {
              if (
                !window.confirm(
                  "Execute real trade if opportunity passes thresholds? Requires execution enabled + executor deployed."
                )
              ) {
                return;
              }
              runScan(false);
            }}
            isDisabled={!player || busy || !config.executionEnabled || !cloudReady}
          >
            Run tick (may execute)
          </Button>
          <Button variant="ghost" onClick={refreshStatus} isDisabled={busy || !cloudReady}>
            Refresh status
          </Button>
        </HStack>

        <Divider borderColor="gray.700" />

        <Box borderWidth="1px" borderColor="gray.700" borderRadius="md" p={4}>
          <Text fontWeight="bold" mb={2}>
            Last scan
          </Text>
          {lastScan?.at && (
            <Text fontSize="xs" color="gray.500" mb={2}>
              {lastScan.at}
            </Text>
          )}
          {best?.passed ? (
            <Text fontSize="sm" color="green.300">
              Best: dir {best.quote?.direction} ${best.quote?.amountInUsd} net ~$
              {(best.netProfitUsd ?? 0).toFixed(2)} ({best.netProfitBps} bps) mode={best.mode}
            </Text>
          ) : (
            <Text fontSize="sm" color="gray.500">
              No opportunity above thresholds yet. Run a dry scan.
            </Text>
          )}
          {lastScan?.executionTxHash && (
            <Text fontSize="sm" mt={2} color="cyan.200">
              Tx: {lastScan.executionTxHash}
            </Text>
          )}
          {lastScan?.executionError && (
            <Text fontSize="sm" mt={2} color="red.300">
              {lastScan.executionError}
            </Text>
          )}
          {Array.isArray(lastScan?.logs) && lastScan.logs.length > 0 && (
            <Box
              mt={3}
              maxH="280px"
              overflowY="auto"
              bg="blackAlpha.400"
              p={2}
              borderRadius="md"
              fontFamily="mono"
              fontSize="xs"
            >
              {lastScan.logs.map((line, i) => (
                <Text key={i} whiteSpace="pre-wrap">
                  {line}
                </Text>
              ))}
            </Box>
          )}
        </Box>
      </VStack>
    </Box>
  );
}

export default function FlashArbBot() {
  return (
    <FlashArbErrorBoundary>
      <FlashArbBotInner />
    </FlashArbErrorBoundary>
  );
}
